import React, { useState, useCallback, useMemo, useEffect } from 'react'
import './Login.css'
import { Tab, ButtonGroup } from '../../style/StyledElement'
import { SignIn } from './SignIn'
import { SignUp } from './SignUp'
import Topbar from '../../components/common/Topbar'
import Copyright from '../../components/common/Copyright'
import Header from './Header'
import Footer from './Footer'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Fade, Alert } from '@mui/material'
import cover from '../../assets/images/3660d6a7-315d-4acc-a1ee-bdf4984d8d21.webp'

// Constants
const AUTH_TYPES = {
  SIGN_IN: 'Sign In',
  CREATE_ACCOUNT: 'Create Account',
}

// Custom hook for URL state management
const useAuthTypeFromUrl = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const getAuthTypeFromUrl = useCallback(() => {
    const mode = searchParams.get('mode')
    return mode === 'signup' ? AUTH_TYPES.CREATE_ACCOUNT : AUTH_TYPES.SIGN_IN
  }, [searchParams])

  const setAuthTypeInUrl = useCallback((type) => {
    const newParams = new URLSearchParams(searchParams)
    if (type === AUTH_TYPES.CREATE_ACCOUNT) {
      newParams.set('mode', 'signup')
    } else {
      newParams.delete('mode')
    }
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  return [getAuthTypeFromUrl(), setAuthTypeInUrl]
}

export const Login = () => {
  const navigate = useNavigate()
  const { userId, isAuthenticated, isLoading } = useSelector((state) => state.auth)
  
  // URL-synced auth type state
  const [activeType, setActiveType] = useAuthTypeFromUrl()
  
  // Local component state
  const [loginState, setLoginState] = useState({})

  // Redirect authenticated users
  useEffect(() => {
    if (isAuthenticated && userId && !isLoading) {
      navigate('/home', { replace: true })
    }
  }, [isAuthenticated, userId, isLoading, navigate])

  // Handle tab switching with URL update
  const handleTabChange = useCallback((type) => {
    setActiveType(type)
    setLoginState({}) // Clear any previous messages
  }, [setActiveType])

  // Handle keyboard navigation for tabs
  const handleTabKeyDown = useCallback((event, type) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleTabChange(type)
    }
    
    // Arrow key navigation
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      const types = Object.values(AUTH_TYPES)
      const currentIndex = types.indexOf(type)
      const nextIndex = event.key === 'ArrowLeft' 
        ? (currentIndex - 1 + types.length) % types.length
        : (currentIndex + 1) % types.length
      handleTabChange(types[nextIndex])
    }
  }, [handleTabChange])

  // Authentication success/error handlers
  const handleAuthSuccess = useCallback((message) => {
    setLoginState({
      message,
      messageType: 'success'
    })
    // Navigation will be handled by the useEffect above
  }, [])

  const handleAuthError = useCallback((message) => {
    setLoginState({
      message,
      messageType: 'error'
    })
  }, [])

  const handleAuthLoading = useCallback((isLoading) => {
    setLoginState(prev => ({
      ...prev,
      isLoading
    }))
  }, [])

  // Clear messages after a delay
  useEffect(() => {
    if (loginState.message) {
      const timer = setTimeout(() => {
        setLoginState(prev => ({ ...prev, message: undefined, messageType: undefined }))
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [loginState.message])

  // Memoized tab buttons for performance
  const tabButtons = useMemo(() => 
    Object.values(AUTH_TYPES).map((type) => (
      <Tab
        key={type}
        active={activeType === type}
        onClick={() => handleTabChange(type)}
        onKeyDown={(e) => handleTabKeyDown(e, type)}
        role="tab"
        tabIndex={activeType === type ? 0 : -1}
        aria-selected={activeType === type}
        aria-controls={`${type.toLowerCase().replace(' ', '-')}-panel`}
      >
        {type}
      </Tab>
    )), 
    [activeType, handleTabChange, handleTabKeyDown]
  )

  // Memoized auth component props
  const authComponentProps = useMemo(() => ({
    onSuccess: handleAuthSuccess,
    onError: handleAuthError,
    onLoadingChange: handleAuthLoading,
    isLoading: loginState.isLoading || isLoading
  }), [handleAuthSuccess, handleAuthError, handleAuthLoading, loginState.isLoading, isLoading])

  // Don't render if user is authenticated (will redirect)
  if (isAuthenticated && userId && !isLoading) {
    return null
  }

  return (
    <div className="login-wrapper">
      <Header>
        <Topbar 
          username={userId} 
          toggleSidebar={false} 
          text="" 
          showAuthButtons={false}
        />
      </Header>
      
      <main className="login-container" role="main">
        <div className="login-content">
          {/* Tab Navigation */}
          <ButtonGroup 
            role="tablist" 
            aria-label="Authentication options"
          >
            {tabButtons}
          </ButtonGroup>

          {/* Status Messages */}
          {loginState.message && (
            <Fade in={Boolean(loginState.message)} timeout={300}>
              <Alert 
                severity={loginState.messageType} 
                sx={{ mt: 2, mb: 2 }}
                onClose={() => setLoginState(prev => ({ 
                  ...prev, 
                  message: undefined, 
                  messageType: undefined 
                }))}
              >
                {loginState.message}
              </Alert>
            </Fade>
          )}

          {/* Auth Forms */}
          <div
            id={`${activeType.toLowerCase().replace(' ', '-')}-panel`}
            role="tabpanel"
            aria-labelledby={`${activeType.toLowerCase().replace(' ', '-')}-tab`}
          >
            <Fade
              in={true}
              key={activeType} // Force re-mount animation on tab change
              timeout={300}
            >
              <div>
                {activeType === AUTH_TYPES.SIGN_IN && (
                  <SignIn {...authComponentProps} />
                )}
                {activeType === AUTH_TYPES.CREATE_ACCOUNT && (
                  <SignUp {...authComponentProps} />
                )}
              </div>
            </Fade>
          </div>
        </div>
      </main>
      
      <Footer>
        <Copyright />
      </Footer>
    </div>
  )
}